import { useState } from 'react';
import { rovingTabIndex, useRovingTabs } from '../components/useRovingTabs';
import {
  AdjustmentsHorizontalIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  PaintBrushIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import type { AssetSort, UserRole } from '@xphoto/shared';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { useAuth } from '../auth/AuthProvider';
import { SettingsApiError, changePassword } from './settings-api';
import { PasswordInput } from '../components/PasswordInput';
import { SharesList, listMyShares } from '../share';
import {
  DEFAULT_SORT_OPTIONS,
  defaultDirForSort,
  getSortPref,
  storeSortPref,
} from '../gallery/default-sort-pref';

/** Sections des réglages (portées par la route `/parametres/:section`). */
export type SettingsSection = 'account' | 'appearance' | 'shares';

/**
 * Écran PARAMÈTRES (épopée Réglages/Admin ; converti en PAGE en 0.18.0).
 *
 * N'est PLUS une modale : c'est une vraie page rendue DANS la zone `main` sous
 * l'en-tête persistant (`AppChrome`) — le fil d'Ariane (« Accueil / Paramètres /
 * … ») reste visible et pilote le retour. Plus de `role="dialog"`, de verrou de
 * défilement, de focus-trap ni de fermeture Échap : la page défile via son propre
 * conteneur `overflow-y-auto`, et « Accueil » (fil d'Ariane) ou le × ramènent à la
 * galerie.
 *
 * Deux sections : COMPTE (lecture seule — email/nom/rôle du compte actif) et
 * CHANGER LE MOT DE PASSE (mot de passe actuel + nouveau + confirmation).
 * Validation client (longueur, correspondance) AVANT l'appel réseau ; un 401
 * distingue « mot de passe actuel incorrect ». Au succès, les autres sessions
 * sont déconnectées côté serveur (message informatif).
 */
interface SettingsModalProps {
  /** Retour à la galerie (bouton ×). La navigation principale passe par le fil d'Ariane. */
  onClose: () => void;
  /**
   * Section active imposée par la ROUTE (`/parametres/:section`). Optionnelle :
   * absente ⇒ la section « Compte » (rétro-compat / tests directs).
   */
  section?: SettingsSection;
  /** Notifie la route d'un changement de section (navigation). */
  onSectionChange?: (section: SettingsSection) => void;
}

/** Clé i18n du libellé de rôle (réutilise la section `auth`). */
const ROLE_LABEL: Record<UserRole, MessageKey> = {
  superadmin: 'auth.roleSuperadmin',
  admin: 'auth.roleAdmin',
  user: 'auth.roleUser',
};

/** Définition d'une section (onglet interne) : id, libellé, icône. */
interface SectionDef {
  id: SettingsSection;
  labelKey: MessageKey;
  icon: React.ReactNode;
}

const SECTIONS: readonly SectionDef[] = [
  {
    id: 'account',
    labelKey: 'settings.tabAccount',
    icon: <UserCircleIcon className="h-5 w-5" aria-hidden="true" />,
  },
  {
    id: 'appearance',
    labelKey: 'settings.tabAppearance',
    icon: <PaintBrushIcon className="h-5 w-5" aria-hidden="true" />,
  },
  {
    id: 'shares',
    labelKey: 'share.mineTitle',
    icon: <LinkIcon className="h-5 w-5" aria-hidden="true" />,
  },
];

const inputClass =
  'h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

export function SettingsModal({
  onClose,
  section = 'account',
  onSectionChange,
}: SettingsModalProps): React.JSX.Element {
  const { t } = useI18n();
  const onTabsKeyDown = useRovingTabs();
  const { user } = useAuth();

  const setSection = (next: SettingsSection): void => {
    if (onSectionChange) onSectionChange(next);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* En-tête de page. */}
      <div className="flex items-center justify-between gap-2 border-b border-border pb-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-fg">
          <AdjustmentsHorizontalIcon className="h-6 w-6" aria-hidden="true" />
          {t('settings.title')}
        </h1>
        <button
          type="button"
          onClick={onClose}
          title={t('settings.close')}
          aria-label={t('settings.close')}
          className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Onglets internes (sections pilotées par la route). */}
      <div
        role="tablist"
        aria-label={t('settings.title')}
        onKeyDown={onTabsKeyDown}
        className="flex gap-1 overflow-x-auto border-b border-border"
      >
        {SECTIONS.map((def) => (
          <button
            key={def.id}
            type="button"
            role="tab"
            aria-selected={section === def.id}
            aria-controls={`settings-${def.id}`}
            tabIndex={rovingTabIndex(section === def.id)}
            onClick={() => setSection(def.id)}
            className={`-mb-px flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
              section === def.id
                ? 'border-brand text-fg'
                : 'border-transparent text-muted hover:text-fg'
            }`}
          >
            {def.icon}
            {t(def.labelKey)}
          </button>
        ))}
      </div>

      {/* Corps défilant (le seul conteneur de défilement de la page). `relative` : ancre
          les descendants `sr-only`/`absolute` à ce conteneur, pas au document (cf. v0.48.0). */}
      <div className="relative min-h-0 flex-1 overflow-y-auto py-4">
        <div className="mx-auto flex max-w-2xl flex-col gap-8">
          {section === 'account' && (
            <section
              id="settings-account"
              role="tabpanel"
              aria-label={t('settings.tabAccount')}
              className="flex flex-col gap-8"
            >
              <p className="text-sm text-muted">{t('settings.intro')}</p>
              {user && (
                <AccountSection
                  role={user.role}
                  email={user.email}
                  displayName={user.displayName}
                />
              )}
              {/* « Changer le mot de passe » est MASQUÉ pour un porteur de LIEN D'ACCÈS
                  (audit A6). Ce visiteur n'a jamais connu le mot de passe du compte
                  partagé par lequel il est entré : il ne peut donc pas remplir le champ
                  « mot de passe actuel » qu'exige `POST /api/auth/change-password`, et
                  le formulaire était un cul-de-sac silencieux. Le signal vient du
                  SERVEUR (`viaSessionLink`, adossé à `sessions.session_link_id`), pas
                  d'une heuristique sur le rôle : c'est la façon dont la session a été
                  OUVERTE qui compte, et rien d'autre ne la décrit. Absent ⇒ `false`
                  (comportement historique : on affiche). */}
              {user?.viaSessionLink === true ? (
                <SessionLinkPasswordNotice />
              ) : (
                <ChangePasswordForm />
              )}
            </section>
          )}
          {section === 'shares' && (
            <section
              id="settings-shares"
              role="tabpanel"
              aria-label={t('share.mineTitle')}
              className="flex flex-col gap-2"
            >
              <h3 className="text-base font-semibold text-fg">{t('share.mineTitle')}</h3>
              <p className="text-sm text-muted">{t('share.mineIntro')}</p>
              {/* La LISTE est partagée avec la console admin ; seule la source change. */}
              <SharesList load={listMyShares} showAuthor={false} />
            </section>
          )}
          {section === 'appearance' && (
            <section
              id="settings-appearance"
              role="tabpanel"
              aria-label={t('settings.tabAppearance')}
              className="flex flex-col gap-6"
            >
              <h3 className="text-base font-semibold text-fg">{t('settings.tabAppearance')}</h3>
              <GallerySortPreference />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/** Libellé i18n de chaque option de tri (réutilise la section `sort.*` du menu Filtrer). */
const SORT_OPTION_LABEL: Record<AssetSort, MessageKey> = {
  random: 'sort.random',
  filename: 'sort.filename',
  takenAt: 'sort.takenAt',
  createdAt: 'sort.createdAt',
  sizeBytes: 'sort.sizeBytes',
  viewCount: 'sort.viewCount',
  rating: 'sort.rating',
};

/**
 * Préférence « Ordre de tri par défaut de l'accueil » (APPARENCE). Lit/écrit la
 * préférence CLIENT (`gallery/default-sort-pref.ts`, localStorage) — comme le thème et
 * la langue. Un `<select>` natif (accessible, étiqueté). Défaut = « Aléatoire ».
 *
 * C'est EXACTEMENT la même valeur que celle éditée par le menu « Trier & filtrer »
 * quand on est sur l'accueil (emplacement `home`) : deux portes d'entrée, un seul
 * réglage. Le tri de NAVIGATION (Dossiers/Tags) n'a volontairement pas de réglage ici —
 * c'est « celui que tu as pris en dernier ».
 *
 * Ce `<select>` ne choisit qu'un CRITÈRE : le sens posé est celui par défaut du critère
 * (`defaultDirForSort`). Un sens précis se choisit dans le menu « Trier & filtrer », qui
 * persiste le couple complet.
 *
 * L'ordre aléatoire est PAR UTILISATEUR (graine persistée côté serveur → même ordre
 * stable sur toutes ses machines) ; il se re-mélange à la demande via le bouton
 * « Re-mélanger » du menu Filtrer de la galerie — plus de sous-réglage de graine ici.
 */
function GallerySortPreference(): React.JSX.Element {
  const { t } = useI18n();
  const [value, setValue] = useState<AssetSort>(() => getSortPref('home').sort);

  const onChange = (next: AssetSort): void => {
    setValue(next);
    storeSortPref('home', { sort: next, dir: defaultDirForSort(next) });
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <div className="flex flex-col gap-1">
        <label htmlFor="settings-default-sort" className="text-sm font-medium text-fg">
          {t('settings.defaultSortLabel')}
        </label>
        <p className="text-sm text-muted">{t('settings.defaultSortHint')}</p>
      </div>
      <select
        id="settings-default-sort"
        value={value}
        onChange={(e) => onChange(e.target.value as AssetSort)}
        className={inputClass}
      >
        {DEFAULT_SORT_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {t(SORT_OPTION_LABEL[opt])}
          </option>
        ))}
      </select>
    </section>
  );
}

/** Section COMPTE (lecture seule) : email, nom affiché, rôle. */
function AccountSection({
  email,
  displayName,
  role,
}: {
  email: string;
  displayName: string;
  role: UserRole;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-fg">{t('settings.accountTitle')}</h3>
      <dl className="grid grid-cols-1 gap-3 rounded-lg border border-border p-4 sm:grid-cols-[auto_1fr]">
        <dt className="text-sm font-medium text-muted">{t('settings.emailLabel')}</dt>
        <dd className="break-all text-sm text-fg">{email}</dd>
        <dt className="text-sm font-medium text-muted">{t('settings.nameLabel')}</dt>
        <dd className="text-sm text-fg">{displayName}</dd>
        <dt className="text-sm font-medium text-muted">{t('settings.roleLabel')}</dt>
        <dd className="text-sm text-fg">{t(ROLE_LABEL[role])}</dd>
      </dl>
    </section>
  );
}

/**
 * Remplace le formulaire de mot de passe pour un porteur de LIEN D'ACCÈS (audit A6).
 *
 * On EXPLIQUE au lieu de simplement masquer : une section qui disparaît sans un mot
 * laisse croire à un bug ou à un droit manquant. Le message dit ce qui est vrai — le
 * compte est partagé, il n'a pas de mot de passe personnel — et vers qui se tourner.
 */
function SessionLinkPasswordNotice(): React.JSX.Element {
  const { t } = useI18n();
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-base font-semibold text-fg">{t('settings.changePasswordTitle')}</h3>
      <p className="flex items-start gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-fg">
        <LinkIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
        <span>{t('settings.passwordViaLink')}</span>
      </p>
    </section>
  );
}

/** Formulaire CHANGER LE MOT DE PASSE. */
function ChangePasswordForm(): React.JSX.Element {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setDone(false);

    // Validation client AVANT l'appel réseau.
    if (newPassword.length < 8) {
      setError(t('settings.errorTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('settings.errorMismatch'));
      return;
    }

    setBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      if (err instanceof SettingsApiError && err.status === 401) {
        setError(t('settings.errorCurrentWrong'));
      } else {
        setError(t('settings.genericError'));
      }
    } finally {
      setBusy(false);
    }
  };

  const errId = 'settings-password-error';
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-fg">{t('settings.changePasswordTitle')}</h3>
      <form
        onSubmit={(e) => void submit(e)}
        className="flex flex-col gap-4 rounded-lg border border-border p-4"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="sp-current" className="text-sm font-medium text-fg">
            {t('settings.currentPasswordLabel')}
          </label>
          <PasswordInput
            id="sp-current"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            aria-invalid={error !== null}
            aria-describedby={error ? errId : undefined}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="sp-new" className="text-sm font-medium text-fg">
            {t('settings.newPasswordLabel')}
          </label>
          <PasswordInput
            id="sp-new"
            required
            minLength={8}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            aria-invalid={error !== null}
            aria-describedby={error ? errId : undefined}
            className={inputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="sp-confirm" className="text-sm font-medium text-fg">
            {t('settings.confirmPasswordLabel')}
          </label>
          <PasswordInput
            id="sp-confirm"
            required
            minLength={8}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            aria-invalid={error !== null}
            aria-describedby={error ? errId : undefined}
            className={inputClass}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {busy ? t('settings.submitting') : t('settings.submit')}
          </button>
          {done && (
            <p role="status" className="text-sm text-fg">
              {t('settings.success')}
            </p>
          )}
          {error && (
            <p
              id={errId}
              role="alert"
              className="inline-flex items-center gap-1 text-sm text-danger"
            >
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {error}
            </p>
          )}
        </div>
      </form>
    </section>
  );
}
